import {
  AfterViewInit,
  Directive,
  EventEmitter,
  Host,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges
} from '@angular/core';
import { CdkDragDrop, CdkDragMove } from '@angular/cdk/drag-drop';
import { deepCopy } from '@delon/util';
import { IComponentDef } from '../../shared/interfaces';
import { DndDropEvent, DropEffect } from '../../ngx-drag-drop';

@Directive()
export abstract class WidgetFormRenderBase {
  /** 父级组件 */
  @Input() parentComponent: IComponentDef;

  /** 要渲染的组件 */
  @Input() component: IComponentDef;

  /** 要渲染的组件发生变更 */
  @Output() widgetChange = new EventEmitter<IComponentDef>();

  /** 当前渲染组件所属index */
  @Input() widgetIndex: number;

  /** 当前选中的组件 */
  @Input() selectComponent: IComponentDef = {};

  /** 当前选中的组件发生变更 */
  @Output() selectComponentChange = new EventEmitter<IComponentDef | null>();

  /** 复制部件 */
  @Output() cloneComponent = new EventEmitter<IComponentDef>();

  /** 删除部件 */
  @Output() deleteComponent = new EventEmitter<IComponentDef>();

  /** 清空子组件 */
  @Output() clearComponent = new EventEmitter<IComponentDef>();

  /** 禁用拖拽 */
  @Input() disableDragDrop = false;

  /** 当前是否存在子组件 */
  get existChildren() {
    return Array.isArray(this.component.children) && this.component.children.length > 0;
  }

  constructor(injector: Injector) {}

  // ============================== 抛事件

  /** 调用克隆组件 */
  callCloneComponent(widget: IComponentDef) {
    this.cloneComponent.emit(widget);
  }

  /** 调用删除组件 */
  callDeleteComponent(widget: IComponentDef) {
    this.selectComponent = null;
    this.selectComponentChange.emit(null);
    this.deleteComponent.emit(widget);
  }

  /** 调用清空子组件 */
  callClearComponent(widget: IComponentDef) {
    this.clearComponent.emit(widget);
  }

  /** 调用选中组件 */
  callSelectWidgetChange(widget: IComponentDef, e?: MouseEvent) {
    this.selectComponent = widget;
    this.selectComponentChange.emit(widget);
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    return false;
  }

  // ============================== 处理

  /** 处理选中组件 */
  handleSelectComponent(widgetOrIndex: number | IComponentDef, e?: MouseEvent) {
    if (typeof widgetOrIndex === 'number') {
      this.selectComponent = this.component.children[widgetOrIndex];
    } else {
      this.selectComponent = widgetOrIndex;
    }
    this.selectComponentChange.emit(this.selectComponent);
    return;
  }

  /** 处理复制组件 */
  handleComponentClone(index: number) {
    let cloneData = deepCopy(this.component.children[index]);
    cloneData.id = `id_${Math.ceil(Math.random() * 99999)}`;
    cloneData.name = `field${Math.ceil(Math.random() * 99999)}`;
    for (const item of cloneData.children) {
      item.id = `id_${Math.ceil(Math.random() * 99999)}`;
      item.name = `field${Math.ceil(Math.random() * 99999)}`;
    }

    this.component.children.splice(index, 0, cloneData);
    this.handleSelectComponent(index + 1);
  }

  /** 处理删除组件 */
  handleComponentDelete(index: number) {
    this.selectComponent = null;
    if (this.component.children.length - 1 === index) {
      if (index === 0) {
        this.selectComponent = null;
      } else {
        this.handleSelectComponent(index - 1);
      }
    } else {
      this.handleSelectComponent(index + 1);
    }

    this.component.children.splice(index, 1);
  }

  /** 清空组件 */
  handleComponentClear(index: number) {
    this.component.children[index].children = [];
  }

  getLabelWidth(item: IComponentDef): string {
    if (typeof item.labelWidth !== 'number') {
      item.labelWidth = 100;
    }

    return `${item.labelWidth}px`;
  }

  // ============================== 拖拽

  /** 拖拽结束 */
  onDragged(effect: DropEffect, parent: IComponentDef, item: IComponentDef) {
    // 排序
    if (effect === 'move') {
      const index = parent.children.indexOf(item);
      parent.children.splice(index, 1);
    }
  }

  /** 放下 */
  onDngDrop(event: DndDropEvent, target?: IComponentDef) {
    if (!target) {
      return;
    }
    if (!Array.isArray(target.children)) {
      target.children = [];
    }
    // 处理index
    let eventIndex = event.index;
    if (typeof eventIndex === 'undefined') {
      eventIndex = target.children.length;
    }
    // 处理数据
    const newWidget = event.data as IComponentDef;

    // copy的数据进行处理
    if (event.dropEffect === 'copy') {
      newWidget.id = `id_${Math.ceil(Math.random() * 99999)}`;
      newWidget.name = `field${Math.ceil(Math.random() * 99999)}`;
      newWidget.placeholder = '';
      newWidget.span = 24;
      if (!Array.isArray(newWidget.children)) {
        newWidget.children = [];
      }
    }

    // 处理目标数组
    if (event.dropEffect === 'copy' || event.dropEffect === 'move') {
      target.children.splice(eventIndex, 0, newWidget);
    }

    if (target.bordered) event.data.bordered = true;
  }
}
